import React, { Fragment } from 'react';
import Swiper, { Navigation, Pagination, Lazy } from 'swiper';
import { useAppConstantsContext } from '@/context/AppConstants';
import { serializedTypes } from '@/reports/types';
import { styled, Grid, Carousel, Link } from '@/theme/index';

export interface ListingDetailsImagesCarouselProps {
  images: serializedTypes.SerializedListingDetails['images'];
  broker: serializedTypes.SerializedListingDetails['brokerage'];
  trackListHubInfo: (eventAlias: string) => void;
}

const CarouselWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  position: 'relative',
  marginBottom: theme.spacing(2),
  '.swiper': {
    height: 314,
  },
  '.swiper-button-next': {
    color: `${theme.palette.primary.contrastText}`,
    right: 0,
    fontSize: 45,
  },
  '.swiper-button-prev': {
    color: `${theme.palette.primary.contrastText}`,
    left: 0,
    fontSize: 45,
  },
  '.swiper-pagination': {
    color: `${theme.palette.primary.contrastText}`,
    fontFamily: "'Lato', sans-serif",
    fontSize: 16,
    fontWeight: 700,
  },

  [theme.breakpoints.up('sm')]: {
    '.swiper': {
      height: 420,
    },
    '.swiper-button-next': {
      right: 20,
      fontSize: 60,
    },
    '.swiper-button-prev': {
      left: 20,
      fontSize: 60,
    },
  },
}));

const config = {
  modules: [Navigation, Pagination, Lazy],
  slidesPerView: 1,
  rewind: true,
  pagination: {
    el: '.swiper-pagination',
    type: 'custom',
    renderCustom(swiper: Swiper, current: number, total: number) {
      return `
          ${current}
          <span className="swiper-pagination-separator">of</span>
          ${total}
        `;
    },
  },
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  lazy: {
    loadOnTransitionStart: true,
    loadPrevNext: true,
  },
};

export function ListingDetailsImagesCarousel({
  images,
  broker,
  trackListHubInfo,
}: ListingDetailsImagesCarouselProps) {
  const {
    images: { imgNoProperty },
  } = useAppConstantsContext();

  let CarouselCardElements = [
    <Grid width="100%" height="inherit" bgcolor="grey.100">
      <img
        alt="no property images"
        src={imgNoProperty}
        style={{
          height: 'inherit',
          width: '100%',
          objectFit: 'contain',
          cursor: 'default',
        }}
      />
    </Grid>,
  ];

  if (images) {
    CarouselCardElements = images.map((image, index) => (
      <Grid width="100%" height="inherit" key={index} bgcolor="grey.100">
        <img
          alt="property images"
          src={image}
          style={{
            height: 'inherit',
            width: '100%',
            objectFit: 'cover',
            cursor: 'default',
          }}
        />
      </Grid>
    ));
  }

  const carouselConfig = {
    ...config,
    onSlideChange: () => {
      trackListHubInfo('viewed_more_photos');
    },
  };

  const handleClick = () => {
    trackListHubInfo('office_website');
  };

  return (
    <>
      <Carousel
        elements={CarouselCardElements}
        config={carouselConfig}
        customWrapper={CarouselWrapper}
      />
      <Link
        href={broker?.website}
        target="_blank"
        rel="noopener"
        color="success.main"
        underline="always"
        fontFamily="Lato, sans-serif"
        fontWeight={700}
        lineHeight={1.625}
        onClick={handleClick}
      >
        {broker?.name}
      </Link>
    </>
  );
}
